/**
 * AduanasTab — Phase 2 Compliance: nationalisation cockpit.
 *
 * Responsibilities:
 *  1. Surface the DIM document (or an empty state with CTA to upload).
 *  2. Inline-edit `numero_dim`, `numero_levante`, `fecha_levante` via
 *     the existing EditableCell pattern.
 *  3. Trigger the Nexo Comercial Excel export (DIAN audit kit).
 */
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EditableCell } from "@/components/ui/EditableCell";
import { FileText, Upload, Download, ShieldCheck, AlertTriangle, Loader2, FileArchive, FileKey } from "lucide-react";
import { CryptoAuditCard } from "@/components/embarque/CryptoAuditCard";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { normaliseTipo } from "@/lib/documentTypes";
import { useNexoComercial } from "@/hooks/useNexoComercial";
import { exportNexoComercial } from "@/lib/exportNexoComercial";
import { useDossierExport } from "@/hooks/useDossierExport";
import { DossierProgressDialog } from "@/components/embarque/DossierProgressDialog";
import { fetchShipmentLandedCost } from "@/hooks/useShipmentLandedCost";
import type { ProrationBase } from "@/lib/landedCostEngine";
import type { DocumentRecord } from "@/hooks/useDocuments";

interface Props {
  embarqueId: string;
  numeroDo: string;
  orgId: string | null | undefined;
  documents: DocumentRecord[];
  isLiquidado: boolean;
  onUploadDim: () => void;
}

export function AduanasTab({
  embarqueId,
  numeroDo,
  orgId,
  documents,
  isLiquidado,
  onUploadDim,
}: Props) {
  const queryClient = useQueryClient();
  const { data: nexoRows, isLoading: loadingNexo } = useNexoComercial(embarqueId);
  const dossier = useDossierExport();
  const [confirmNoDimOpen, setConfirmNoDimOpen] = useState(false);

  // Locate the DIM document (most recent first).
  const dimDoc = useMemo(() => {
    return documents
      .filter((d) => normaliseTipo(d.tipo_documento) === normaliseTipo("Declaración de Importación"))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
  }, [documents]);

  const handleDossierZip = () => {
    if (!dimDoc) {
      setConfirmNoDimOpen(true);
      return;
    }
    dossier.start(embarqueId, numeroDo);
  };

  const handleConfirmNoDim = () => {
    setConfirmNoDimOpen(false);
    dossier.start(embarqueId, numeroDo);
  };

  const updateDimField = async (field: "numero_dim" | "numero_levante" | "fecha_levante", value: string) => {
    if (!dimDoc) return;
    const cleaned = value.trim() === "" ? null : value.trim();
    const { error } = await supabase
      .from("registro_documentos")
      .update({ [field]: cleaned } as any)
      .eq("id", dimDoc.id);
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ["embarque-documents", orgId, embarqueId] });
    queryClient.invalidateQueries({ queryKey: ["nexo-comercial", embarqueId] });
    toast.success("Datos de nacionalización actualizados");
  };

  const handleExport = async () => {
    if (!nexoRows || nexoRows.length === 0) {
      toast.error("No hay ítems para exportar en este embarque.");
      return;
    }
    try {
      // ── Opción C: enriquecer costos con el RPC `calculate_landed_cost_preview`
      // (misma fuente que la trazabilidad / dossier). En embarques NO liquidados
      // `base_inventario.costo_nacionalizado_unitario_cop` está en 0; el RPC
      // entrega el costo proyectado vivo.
      let costsByItemId: Record<string, number> | undefined;
      let isProyectado = false;
      try {
        const { data: emb } = await supabase
          .from("embarques")
          .select("id, org_id, trm_aduana, liquidado")
          .eq("id", embarqueId)
          .maybeSingle();
        const embOrgId = (emb as any)?.org_id as string | undefined;
        isProyectado = !(emb as any)?.liquidado;

        const COMMERCIAL_TYPES = new Set(["Factura Internacional", "Factura de Compras"]);
        const docsComerciales = (documents as any[]).filter((d) =>
          COMMERCIAL_TYPES.has(d.tipo_documento ?? ""),
        );
        const docsComercialesIds = docsComerciales.map((d) => d.id);

        if (embOrgId && docsComercialesIds.length > 0) {
          const { data: gastos } = await supabase
            .from("gastos_embarque")
            .select("id, concepto, monto, monto_base, moneda, trm_pago, base_prorrateo, aplica_costeo_inventario")
            .eq("embarque_id", embarqueId)
            .is("deleted_at", null);

          const { data: inv } = await supabase
            .from("base_inventario")
            .select(
              "id, sku, descripcion, cantidad, precio_unitario, sub_total, peso_bruto_kg, hs_code, aplica_tlc, beneficio_aduanero, iva_override, documento_id",
            )
            .in("documento_id", docsComercialesIds)
            .is("deleted_at", null);

          const invList = (inv ?? []).filter((i: any) => Number(i.precio_unitario ?? 0) > 0);

          if (invList.length > 0) {
            const engineDocs = docsComerciales.map((d: any) => ({
              id: d.id,
              moneda: d.moneda,
              costo_flete: d.costo_flete,
              seguro: d.seguro,
              otros_cargos: d.otros_cargos,
              impuestos: d.impuestos,
            }));
            const engineItems = invList.map((i: any) => ({
              id: i.id,
              documento_id: i.documento_id,
              sku: i.sku,
              descripcion: i.descripcion,
              precio_unitario: i.precio_unitario,
              cantidad: i.cantidad,
              sub_total: i.sub_total,
              peso_bruto_kg: i.peso_bruto_kg,
              hs_code: i.hs_code,
              aplica_tlc: i.aplica_tlc,
              beneficio_aduanero: i.beneficio_aduanero,
              iva_override: i.iva_override,
            }));
            const engineGastos = ((gastos ?? []) as any[]).map((g) => ({
              id: g.id,
              concepto: g.concepto ?? "",
              monto: Number(g.monto ?? 0),
              monto_base: g.monto_base != null ? Number(g.monto_base) : null,
              moneda: g.moneda ?? "COP",
              base_prorrateo: (g.base_prorrateo ?? "Valor") as ProrationBase,
              trm_pago: g.trm_pago,
              aplica_costeo_inventario: g.aplica_costeo_inventario ?? true,
            }));
            const beneficioByItemId: Record<string, "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA"> = {};
            invList.forEach((i: any) => {
              const explicit = i.beneficio_aduanero as any;
              beneficioByItemId[i.id] = explicit ?? (i.aplica_tlc ? "TLC_PURO" : "GENERAL");
            });

            const trm = Number((emb as any)?.trm_aduana ?? 0);
            const lc = await fetchShipmentLandedCost({
              orgId: embOrgId,
              documents: engineDocs,
              items: engineItems,
              gastos: engineGastos,
              trmAduana: trm > 0 ? trm : null,
              beneficioByItemId,
            });

            costsByItemId = {};
            lc.items.forEach((r) => {
              costsByItemId![r.itemId] = Number(r.landedCostUnitarioCOP ?? 0);
            });
          }
        }
      } catch (e: any) {
        console.warn("[NexoComercial] No se pudo enriquecer con RPC, exportando con costos del freeze.", e);
        toast.warning("Costos en vivo no disponibles", {
          description: "Exportando con valores congelados de base_inventario.",
        });
      }

      await exportNexoComercial(numeroDo, nexoRows, { costsByItemId, isProyectado });
      toast.success(`Nexo_Comercial_${numeroDo}.xlsx descargado`);
    } catch (err: any) {
      toast.error("Error al exportar", { description: err?.message });
    }
  };

  // ── Confirm dialog (re-used by both states) ─────────────────────
  const confirmDialog = (
    <AlertDialog open={confirmNoDimOpen} onOpenChange={setConfirmNoDimOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            Dossier Incompleto
          </AlertDialogTitle>
          <AlertDialogDescription>
            No se ha detectado una <strong>Declaración de Importación (DIM)</strong> en este
            embarque. El paquete generado <strong>no tendrá validez legal completa</strong> ante
            la DIAN. ¿Deseas continuar con la descarga preliminar?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmNoDim}>
            Descargar de todas formas
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // ── Empty state: no DIM uploaded yet ─────────────────────────────
  if (!dimDoc) {
    return (
      <div className="h-full w-full overflow-y-auto">
        <div className="flex-1 flex flex-col items-center justify-center w-full p-8 text-center bg-muted/20 border-2 border-dashed border-border rounded-xl gap-4 m-1 min-h-[60vh]">
          <div className="h-24 w-24 rounded-full bg-muted/40 flex items-center justify-center ring-8 ring-muted/10">
            <FileText className="h-12 w-12 text-muted-foreground/70" />
          </div>
          <h3 className="text-2xl font-semibold text-foreground tracking-tight">
            Blindaje DIAN: Documento de Importación requerido
          </h3>
          <p className="max-w-md text-sm text-muted-foreground leading-relaxed">
            Este módulo genera automáticamente el reporte de Nexo Comercial que exige la DIAN para auditorías. Para activarlo y desbloquear el cierre financiero del embarque, sube la Declaración de Importación (DIM) oficial.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button onClick={onUploadDim} className="gap-2" disabled={isLiquidado} size="lg">
              <Upload className="h-4 w-4" />
              Subir DIM
            </Button>
            <Button
              onClick={handleDossierZip}
              variant="outline"
              size="lg"
              className="gap-2"
              disabled={dossier.busy || documents.length === 0}
            >
              {dossier.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileArchive className="h-4 w-4" />}
              Descargar Dossier Preliminar (ZIP)
            </Button>
          </div>
          {isLiquidado && (
            <p className="text-xs text-muted-foreground">
              El embarque está liquidado. Contacta al administrador para modificaciones.
            </p>
          )}
          {documents.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              Sin documentos cargados aún — el ZIP estará vacío.
            </p>
          )}
        </div>
        <DossierProgressDialog
          open={dossier.open}
          progress={dossier.progress}
          busy={dossier.busy}
          onClose={dossier.close}
        />
        {confirmDialog}
      </div>
    );
  }

  // ── Active state: DIM exists ────────────────────────────────────
  const hasNumeroDim = !!(dimDoc as any).numero_dim;
  const hasLevante = !!(dimDoc as any).numero_levante;

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="space-y-4 pb-10 px-1">
      {/* DIM Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              Declaración de Importación
            </CardTitle>
            <div className="flex items-center gap-1.5">
              {hasNumeroDim && hasLevante && (
                <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 gap-1 text-[10px]">
                  <ShieldCheck className="h-3 w-3" /> Nacionalizado
                </Badge>
              )}
              {hasNumeroDim && !hasLevante && (
                <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 gap-1 text-[10px]">
                  <AlertTriangle className="h-3 w-3" /> Pendiente Levante
                </Badge>
              )}
              {!hasNumeroDim && (
                <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 gap-1 text-[10px]">
                  <AlertTriangle className="h-3 w-3" /> Sin Datos DIM
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-[12px] text-muted-foreground">
            Archivo: <span className="font-mono text-foreground">{dimDoc.no_documento}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
            <FieldRow
              label="Número DIM"
              hint="Sticker DIAN"
              value={(dimDoc as any).numero_dim}
              onSave={(v) => updateDimField("numero_dim", v)}
              disabled={isLiquidado}
            />
            <FieldRow
              label="Número Levante"
              hint="Autorización DIAN"
              value={(dimDoc as any).numero_levante}
              onSave={(v) => updateDimField("numero_levante", v)}
              disabled={isLiquidado}
            />
            <FieldRow
              label="Fecha Levante"
              hint="YYYY-MM-DD"
              value={(dimDoc as any).fecha_levante}
              onSave={(v) => updateDimField("fecha_levante", v)}
              disabled={isLiquidado}
              type="text"
            />
          </div>

          {!hasLevante && hasNumeroDim && (
            <Alert className="border-amber-500/40 bg-amber-500/5 mt-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertTitle className="text-[12px] font-semibold text-amber-700 dark:text-amber-400">
                Falta el Número de Levante
              </AlertTitle>
              <AlertDescription className="text-[11px] leading-snug">
                Sin Levante la mercancía no puede salir del puerto. Esta es una alerta opcional y
                no bloquea la liquidación, pero sí afecta el Nexo Comercial entregado a DIAN.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Export Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4 text-primary" />
            Nexo Comercial — Kit Antisanciones DIAN
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[12px] text-muted-foreground leading-snug">
            Genera un archivo Excel que rastrea cada SKU del inventario hasta su factura origen y la
            DIM que lo nacionalizó. Indispensable para auditorías y demostrar trazabilidad legal.
          </p>
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-[11px] text-muted-foreground">
              {loadingNexo
                ? "Cargando ítems…"
                : `${nexoRows?.length ?? 0} ítem(s) listo(s) para exportar`}
            </div>
            <Button
              size="lg"
              onClick={handleExport}
              disabled={loadingNexo || !nexoRows || nexoRows.length === 0}
              className="gap-2"
            >
              {loadingNexo ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              📥 Exportar Nexo Comercial (DIAN)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dossier ZIP Card — full DIAN bundle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileArchive className="h-4 w-4 text-primary" />
            Dossier DIAN — Paquete Completo (ZIP)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[12px] text-muted-foreground leading-snug">
            Descarga un único archivo ZIP con todos los documentos del embarque renombrados según la
            normativa DIAN (01_Declaración_Importación, 03_Factura_Comercial…) más un índice maestro
            (00_Indice_Dossier.xlsx) con carátula, resumen financiero y nexo comercial. Listo para
            entregar a auditores o adjuntar a una respuesta a requerimiento.
          </p>
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-[11px] text-muted-foreground">
              {documents.length} documento(s) del embarque se incluirán
            </div>
            <Button
              size="lg"
              onClick={handleDossierZip}
              disabled={dossier.busy || documents.length === 0}
              className="gap-2"
            >
              {dossier.busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileKey className="h-4 w-4" />
              )}
              Descargar Dossier Certificado
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Crypto audit card — DIAN-03 immutable signature */}
      <CryptoAuditCard embarqueId={embarqueId} />
      </div>

      <DossierProgressDialog
        open={dossier.open}
        progress={dossier.progress}
        busy={dossier.busy}
        onClose={dossier.close}
      />
      {confirmDialog}
    </div>
  );
}

// ── Local helper: labelled inline-editable row ─────────────────────
function FieldRow({
  label,
  hint,
  value,
  onSave,
  disabled,
  type = "text",
}: {
  label: string;
  hint?: string;
  value: any;
  onSave: (v: string) => Promise<void>;
  disabled?: boolean;
  type?: "text" | "number";
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          {label}
        </span>
        {hint && <span className="text-[9px] text-muted-foreground italic">{hint}</span>}
      </div>
      <div className="px-2 py-1.5 rounded-md border border-input bg-background text-[13px] font-mono min-h-[34px] flex items-center">
        <EditableCell
          value={value}
          onSave={onSave}
          disabled={disabled}
          type={type}
          displayFormat={(v) =>
            v ? (
              <span className="text-foreground">{String(v)}</span>
            ) : (
              <span className="text-muted-foreground italic">— sin definir —</span>
            )
          }
        />
      </div>
    </div>
  );
}
